import { useState } from 'react';
import config from '../config/config';
import { useAuth } from '../contexts/AuthContext';
import authHeader from '../config/authHeader';

const useMessagerie = () => {
    const { token, logout } = useAuth();

    const [conversations, setConversations] = useState([]);
    const [messages, setMessages] = useState([]);
    const [participants, setParticipants] = useState([]);
    const [messagesLus, setMessagesLus] = useState([]);
    const [lastSeenMessage, setLastSeenMessage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchHelper = async (url, options = {}, setState = null) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(url, {
                ...options,
                headers: authHeader(),
            });

            if (response.status === 401) return logout();
            if (!response.ok) throw new Error(`Erreur lors de la requête : ${url}`);

            const data = await response.json();
            if (setState) setState(data);
            return data;
        } catch (err) {
            setError(err.message);
            return null;
        } finally {
            setLoading(false);
        }
    };

    const postForm = async (url, payload) => {
        setLoading(true);
        setError(null);
        const formData = new FormData();
        for (const key in payload) {
            if (payload.hasOwnProperty(key)) {
                formData.append(key, payload[key]);
            }
        }

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: authHeader(),
                body: formData,
            });

            if (response.status === 401) return logout();
            if (!response.ok) throw new Error(`Erreur lors de l'envoi POST à ${url}`);

            return await response.json();
        } catch (err) {
            setError(err.message);
            return null;
        } finally {
            setLoading(false);
        }
    };

    // === Méthodes principales ===
    const fetchConversations = () =>
        fetchHelper(`${config.apiBaseUrl}/4DACTION/KST_getConversationsByUserUUID`, {}, setConversations);

    const fetchMessagesByConversationUUID = (uuid) =>
        fetchHelper(`${config.apiBaseUrl}/4DACTION/KST_getMessagesByConvUUID?uuid=${uuid}`, {}, setMessages);

    const fetchParticipantsByConversationUUID = (uuid) =>
        fetchHelper(`${config.apiBaseUrl}/4DACTION/KST_getParticipantsByConversationUUID?uuid=${uuid}`, {}, setParticipants);

    const fetchMessagesLusByUser = () =>
        fetchHelper(`${config.apiBaseUrl}/4DACTION/KST_getMessagesLusByUserUUID`, {}, setMessagesLus);

    const fetchLastSeenMessage = async (uuidConv) => {
        const data = await fetchHelper(`${config.apiBaseUrl}/4DACTION/KST_getLastSeenMessage?uuid=${uuidConv}`);
        if (Array.isArray(data) && data.length > 0) {
            setLastSeenMessage(data[0]);
            return data[0];
        } else {
            setLastSeenMessage(null);
            return null;
        }
    };

    const createMessage = (type, message, uuidConv) =>
        postForm(`${config.apiBaseUrl}/4DACTION/KST_createMessage`, {
            Type: type,
            Message: message,
            UUID_Conversation: uuidConv
        });

    const createConversation = (payload) =>
        postForm(`${config.apiBaseUrl}/4DACTION/KST_createConversation`, payload);

    const addParticipantsToConv = async (uuidConv, payloadUserUUIDs) => {
        postForm(`${config.apiBaseUrl}/4DACTION/KST_addParticipantsToConv`, {
            UUID_Conversation: uuidConv,
            payloadUserUUIDs: JSON.stringify(payloadUserUUIDs)
        });
    };

    return {
        conversations,
        messages,
        participants,
        messagesLus,
        lastSeenMessage,
        loading,
        error,
        fetchConversations,
        fetchMessagesByConversationUUID,
        fetchParticipantsByConversationUUID,
        fetchMessagesLusByUser,
        fetchLastSeenMessage,
        createMessage,
        createConversation,
        addParticipantsToConv
    };
};

export default useMessagerie;